<template>
  <section
    class="parking-map-dialog"
    :class="{'is-drawing': isDrawing}"
    :style="{'--parking-map-color': color}"
    :aria-label="parkingName ? `Разметка парковки ${parkingName}` : 'Разметка парковки'"
  >
    <header class="parking-map-dialog__header">
      <button type="button" aria-label="Закрыть разметку" @click="closeDialog()">
        <svg aria-hidden="true"><use xlink:href="#icon-arrow-right" /></svg>
      </button>
      <h2>Разметка на карте</h2>
    </header>

    <div class="parking-map-dialog__map-wrapper">
      <v-map
        ref="map"
        class="parking-map-dialog__map"
        :center="mapCenter"
        :min-zoom="mapMinZoom"
        :max-zoom="20"
        :options="mapOptions"
        :zoom="mapZoom"
        @click="addPoint"
        @ready="handleMapReady"
      >
        <v-tile-layer
          v-if="mapTilesUrl"
          :url="mapTilesUrl"
        />

        <v-marker
          v-for="camera in mapCameras"
          :key="`camera-${camera.camera_number}`"
          :icon="cameraIcon"
          :lat-lng="camera.position"
          :draggable="false"
          :options="{bubblingMouseEvents: false}"
          :title="camera.title || camera.camera_number"
          :z-index-offset="-1000"
        >
          <v-popup :options="cameraPopupOptions">
            <article class="parking-map-dialog__camera-card">
              <header class="parking-map-dialog__camera-heading">
                <strong>{{ camera.title || camera.camera_number }}</strong>
              </header>
              <div class="parking-map-dialog__camera-preview">
                <img
                  v-if="camera.preview_url && !failedCameraPreviews[camera.camera_number]"
                  :src="camera.preview_url"
                  :alt="`Превью камеры ${camera.title || camera.camera_number}`"
                  @error="$set(failedCameraPreviews, camera.camera_number, true)"
                >
                <span v-else>Превью недоступно</span>
              </div>
              <div v-if="camera.address" class="parking-map-dialog__camera-address">
                {{ camera.address }}
              </div>
            </article>
          </v-popup>
        </v-marker>

        <v-polyline
          v-if="polygon.length >= 2 && !canSave"
          ref="shapeLayer"
          :lat-lngs="leafletPolygon"
          color="#39495D"
          :interactive="false"
          :weight="2"
        />

        <v-polygon
          v-if="canSave"
          ref="shapeLayer"
          :lat-lngs="leafletPolygon"
          color="#39495D"
          :fill="true"
          :fill-color="color"
          :fill-opacity="0.3"
          :interactive="false"
          :weight="2"
        />

        <v-marker
          v-for="(point, index) in polygon"
          :key="index"
          :draggable="true"
          :icon="vertexIcon"
          :lat-lng="[point.lat, point.lon]"
          :options="{bubblingMouseEvents: false}"
          @drag="queuePointDrag(index, $event)"
          @dragend="finishPointDrag(index, $event)"
        />
      </v-map>

      <ParkingMapMarkupToolbar
        class="parking-map-dialog__toolbar"
        :is-drawing="isDrawing"
        :points-count="polygon.length"
        :can-save="canSave"
        @toggle-drawing="toggleDrawing"
        @clear="clearPolygon"
        @save="savePolygon"
      >
        <template #drawing-hint>
          <transition name="parking-map-hint">
            <div
              v-if="showHint"
              class="parking-map-dialog__tooltip"
              role="status"
            >
              Сделайте разметку на карте, для пользователей мобильного приложения
              Умный дом, так она будет отображаться в приложении
            </div>
          </transition>
        </template>
      </ParkingMapMarkupToolbar>
    </div>
  </section>
</template>

<script>
import L from "leaflet";
import {
  LMap,
  LMarker,
  LPolygon,
  LPolyline,
  LPopup,
  LTileLayer,
} from "vue2-leaflet";
import iconRetinaUrl from "@/assets/img/icons/map-pointer2х.png";
import iconUrl from "@/assets/img/icons/map-pointer1х.png";
import shadowUrl from "leaflet/dist/images/marker-shadow.png";
import {methodsForDialogMixin} from "@/components/pacs/mixins.js";
import ParkingMapMarkupToolbar from "@/components/pacs/analytics/parkingDetection/ParkingMapMarkupToolbar.vue";
import {
  isParkingPolygonValid,
  MAX_PARKING_POLYGON_POINTS,
} from "@/components/pacs/analytics/parkingDetection/parkingPolygon.js";
import {MAP_TILES_URL} from "@/utils/consts.js";

const TOOLTIP_DURATION = 10000;
const clonePolygon = polygon => JSON.parse(JSON.stringify(polygon));
const roundCoordinate = coordinate => Number(coordinate.toFixed(7));
const normalizeCenter = center => {
  const latitude = Array.isArray(center) ? center[0] : center && center.lat;
  const longitude = Array.isArray(center)
    ? center[1]
    : center && (center.lon === 0 || center.lon ? center.lon : center.lng);

  const coordinates = [latitude, longitude];
  if (coordinates.some(value => (typeof value !== "number" && typeof value !== "string")
    || (typeof value === "string" && !value.trim()))) return null;

  const [lat, lon] = coordinates.map(Number);
  return Number.isFinite(lat) && Number.isFinite(lon) && Math.abs(lat) <= 90 && Math.abs(lon) <= 180
    ? [lat, lon] : null;
};
const getMapCameras = cameras => cameras.reduce((result, camera) => {
  if (!camera) return result;
  const position = normalizeCenter([camera.latitude, camera.longitude]);
  // Координаты [0, 0] возвращаются сервером, когда положение камеры не задано.
  if (position && (position[0] !== 0 || position[1] !== 0)) {
    result.push({...camera, position});
  }
  return result;
}, []);
const calculateCenter = positions => {
  const sum = positions.reduce((result, point) => [
    result[0] + point[0],
    result[1] + point[1],
  ], [0, 0]);

  return sum.map(coordinate => coordinate / positions.length);
};
const cameraIcon = L.icon({
  iconRetinaUrl,
  iconUrl,
  iconSize: [37, 52],
  iconAnchor: [18, 51],
  popupAnchor: [0, -47],
  shadowAnchor: [12, 39],
  shadowUrl,
});
const vertexIcon = L.divIcon({
  className: "parking-map-dialog__vertex-marker",
  html: "<span></span>",
  iconAnchor: [5, 5],
  iconSize: [10, 10],
});

/**
 * Модальное окно разметки контура парковки кликами по Leaflet-карте.
 */
export default {
  name: "ParkingMapMarkupDialog",
  components: {
    ParkingMapMarkupToolbar,
    "v-map": LMap,
    "v-marker": LMarker,
    "v-polygon": LPolygon,
    "v-polyline": LPolyline,
    "v-popup": LPopup,
    "v-tile-layer": LTileLayer,
  },
  mixins: [methodsForDialogMixin],
  props: {
    parkingName: {
      type: String,
      default: "",
    },
    initialPolygon: {
      type: Array,
      default: () => [],
    },
    initialCenter: {
      type: [Array, Object],
      default: null,
    },
    cameras: {
      type: Array,
      default: () => [],
    },
    color: {
      type: String,
      default: "#2FC4B2",
    },
  },
  data() {
    const polygon = clonePolygon(this.initialPolygon);
    const positions = polygon.length
      ? polygon.map(point => [point.lat, point.lon])
      : getMapCameras(this.cameras).map(camera => camera.position);
    const contextualCenter = positions.length
      ? calculateCenter(positions)
      : normalizeCenter(this.initialCenter);

    return {
      cameraPopupOptions: {
        className: "parking-map-dialog__camera-popup",
        minWidth: 240,
        maxWidth: 240,
        autoPanPaddingTopLeft: [20, 20],
        autoPanPaddingBottomRight: [20, 100],
      },
      dragAnimationFrame: null,
      failedCameraPreviews: {},
      isDrawing: false,
      mapCenter: contextualCenter || [0, 0],
      mapMinZoom: 2,
      mapZoom: contextualCenter ? 17 : 2,
      mapOptions: {
        attributionControl: false,
        doubleClickZoom: false,
        preferCanvas: false,
        zoomControl: false,
      },
      mapResizeTimer: null,
      mapTilesUrl: MAP_TILES_URL,
      polygon,
      pendingPointDrag: null,
      showHint: true,
      tooltipTimer: null,
    };
  },
  computed: {
    canAddPoints() {
      return this.polygon.length < MAX_PARKING_POLYGON_POINTS;
    },
    canSave() {
      return isParkingPolygonValid(this.polygon);
    },
    leafletPolygon() {
      return this.polygon.map(point => [point.lat, point.lon]);
    },
    mapCameras() {
      return getMapCameras(this.cameras);
    },
    cameraIcon() {
      return cameraIcon;
    },
    vertexIcon() {
      return vertexIcon;
    },
  },
  mounted() {
    this.tooltipTimer = setTimeout(() => {
      this.showHint = false;
    }, TOOLTIP_DURATION);
  },
  beforeDestroy() {
    this.cancelPendingPointDrag();
    clearTimeout(this.mapResizeTimer);
    clearTimeout(this.tooltipTimer);
  },
  methods: {
    handleMapReady(mapObject) {
      this.mapResizeTimer = setTimeout(() => {
        mapObject.invalidateSize();
        this.fitMapContent(mapObject);
      }, 80);
    },
    fitMapContent(mapObject = this.$refs.map && this.$refs.map.mapObject) {
      const positions = [...this.leafletPolygon, ...this.mapCameras.map(camera => camera.position)];
      if (!mapObject || !positions.length) return;

      const bounds = L.latLngBounds(positions);
      if (bounds.isValid()) {
        mapObject.fitBounds(bounds, {padding: [80, 80], maxZoom: 18});
      }
    },
    addPoint(event) {
      const canAddPoint = this.isDrawing
        && this.canAddPoints
        && event
        && event.latlng;
      if (!canAddPoint) return;

      const position = L.latLng(event.latlng);
      this.polygon.push({
        lon: roundCoordinate(position.lng),
        lat: roundCoordinate(position.lat),
      });

      if (!this.canAddPoints) {
        this.isDrawing = false;
      }
    },
    pointFromDragEvent(event) {
      if (event && event.target && typeof event.target.getLatLng === "function") {
        return event.target.getLatLng();
      }
      return event && event.latlng ? L.latLng(event.latlng) : null;
    },
    queuePointDrag(index, event) {
      const position = this.pointFromDragEvent(event);
      if (!position) return;

      this.pendingPointDrag = {index, position};
      if (this.dragAnimationFrame !== null) return;

      this.dragAnimationFrame = requestAnimationFrame(() => {
        const pendingPointDrag = this.pendingPointDrag;
        this.dragAnimationFrame = null;
        this.pendingPointDrag = null;

        if (pendingPointDrag) {
          this.renderDraggedPoint(pendingPointDrag.index, pendingPointDrag.position);
        }
      });
    },
    finishPointDrag(index, event) {
      const position = this.pointFromDragEvent(event);
      if (!position) return;

      this.cancelPendingPointDrag();
      this.renderDraggedPoint(index, position);
      this.updatePoint(index, position);
    },
    renderDraggedPoint(index, position) {
      const shapeComponent = this.$refs.shapeLayer;
      if (!shapeComponent || !shapeComponent.mapObject) return;

      const latLngs = this.leafletPolygon.map((point, pointIndex) => (
        pointIndex === index ? [position.lat, position.lng] : point
      ));
      shapeComponent.mapObject.setLatLngs(latLngs);
    },
    cancelPendingPointDrag() {
      if (this.dragAnimationFrame !== null) {
        cancelAnimationFrame(this.dragAnimationFrame);
      }
      this.dragAnimationFrame = null;
      this.pendingPointDrag = null;
    },
    updatePoint(index, latLng) {
      const position = L.latLng(latLng);
      this.$set(this.polygon, index, {
        lon: roundCoordinate(position.lng),
        lat: roundCoordinate(position.lat),
      });
    },
    toggleDrawing() {
      this.isDrawing = this.canAddPoints && !this.isDrawing;
    },
    clearPolygon() {
      this.cancelPendingPointDrag();
      this.polygon = [];
    },
    savePolygon() {
      if (!this.canSave) return;
      this.closeDialog(clonePolygon(this.polygon));
    },
  },
};
</script>
